import { useFormContext } from 'react-hook-form';
import type { FacebookPage } from '../types';
import { usePagination } from 'hooks/usePagination';
import { useFacebookPages } from '../api';
import { useFacebookPagesColumns, useGridSelection } from '../hooks';
import { BaseGrid } from 'components/common';
import { SuspenseBoundary } from 'components/common';
import { memo, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useListParams } from 'hooks';
import { debounce, isEmpty } from 'lodash-es';
import { usePermissionGuard } from '../../../login';

const FacebookPagesGridComponent = memo(() => {
  const { accessToChangeFacebookadaccount } = usePermissionGuard();
  const { watch } = useFormContext();
  const navigate = useNavigate();
  const pagination = usePagination([], 50);
  const { setQ, q: urlQ } = useListParams();

  const [debouncedFilter, setDebouncedFilter] = useState({
    q: urlQ ?? undefined,
  });

  const filterByQ = watch('q') as string;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedUpdate = useCallback(
    debounce((q: string) => {
      setDebouncedFilter({ q });
      setQ(q ?? undefined);
    }, 500),
    [debounce, isEmpty, setDebouncedFilter, setQ],
  );

  useEffect(() => {
    debouncedUpdate(filterByQ);
    return () => {
      debouncedUpdate.cancel();
    };
  }, [filterByQ, debouncedUpdate]);

  const { selectedRows, handleSelectionChange } = useGridSelection(
    'selectedFacebookPages',
  );

  const { desc } = useListParams();

  const columns = useFacebookPagesColumns();

  const {
    data: { data: facebookPages, count },
    isLoading,
  } = useFacebookPages({
    params: {
      ...(debouncedFilter.q && { name: debouncedFilter.q }),
      desc,
      order_by: 'name',
      limit: pagination.perPage,
      offset: pagination.page,
    },
  });

  const handleClick = useCallback(
    (id: number) => {
      accessToChangeFacebookadaccount && navigate(`/cabinet/facebook/facebook-pages/${id}`);
    },
    [navigate, accessToChangeFacebookadaccount],
  );

  return (
    <BaseGrid<FacebookPage>
      rows={facebookPages}
      columns={columns}
      count={count}
      isLoading={isLoading}
      pagination={pagination}
      rowSelectionModel={selectedRows}
      onRowSelectionModelChange={handleSelectionChange}
      getRowId={(row) => row.id}
      onRowClick={(params) => {
        handleClick((params.row as FacebookPage).id);
      }}
    />
  );
});

FacebookPagesGridComponent.displayName = 'FacebookPagesGridComponent';

const FacebookPagesGrid = () => {
  return (
    <SuspenseBoundary>
      <FacebookPagesGridComponent />
    </SuspenseBoundary>
  );
};

export default FacebookPagesGrid;
